import { Box, Paper, Stack, Text } from '@mantine/core';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import type { CalendarEvent } from 'src/meetings/models';
import { getAllClientParticipants } from 'src/meetings/utils/event-utils';
import { v4 as uuidv4 } from 'uuid';
import { NavLink } from 'react-router';
import { useFileUpload } from 'src/data-capture/hooks/file-uploads/useFileUpload';
import RecordingProcessingFailureMessage from 'src/meetings/detail/tabs/RecordingProcessingFailureMessage';
import { Transcript } from './transcript/types';

type Props = {
  event: CalendarEvent;
  transcript: Transcript;
};

export const SummaryTab = ({ event, transcript }: Props) => {
  const clientParticipants = getAllClientParticipants(event);
  const firstClientId = clientParticipants[0]?.participant.client?.id;
  const { file: recordingDataUploadFile } = useFileUpload(transcript?.dataUploadFileId, {
    clientId: firstClientId,
    enabled: clientParticipants.length === 1,
  });

  const getSummaryContent = () => {
    if (transcript?.summary) {
      return (
        <Stack gap="xs">
          <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
            Overview
          </Text>
          <Text size="sm" fw={FONT_WEIGHTS.medium} c="gray.10">
            {transcript?.summary}
          </Text>
        </Stack>
      );
    }

    return (
      <Box>
        <Text size="sm" c="gray.7">
          {getSummaryMissingContent()}
        </Text>
      </Box>
    );
  };

  const getSummaryMissingContent = () => {
    if (!event.hasTranscription) {
      return 'A transcription is required to generate a summary.';
    }

    if (transcript?.summaryStatus === 'ERROR' || transcript?.status === 'FAILED') {
      return <RecordingProcessingFailureMessage />;
    }

    return 'No summary available';
  };

  return (
    <>
      {getSummaryContent()}

      {recordingDataUploadFile &&
        !recordingDataUploadFile.isResolved &&
        clientParticipants.length === 1 && (
          <Stack gap="xs" mt={'lg'}>
            <Text size="md" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Suggested actions
            </Text>
            <Paper variant="card" w={'328px'}>
              <Text size="sm" fw={FONT_WEIGHTS.semibold} c="gray.10">
                Add data to client record
              </Text>
              <Text mt="xxxs" mb="xs" size="xs" fw={FONT_WEIGHTS.medium} c="gray.10">
                Extract client data from this meeting note to add/update their record.
              </Text>
              <NavLink
                to={`/client/${firstClientId}/detected-entities?fileId=${transcript?.dataUploadFileId}&fileName=${event.title}&guid=${uuidv4()}&originMeetingId=${event.id}`}
                style={{ textDecoration: 'none' }}
              >
                <Text size="xs" fw={FONT_WEIGHTS.medium} c="brand.6">
                  Review data
                </Text>
              </NavLink>
            </Paper>
          </Stack>
        )}
    </>
  );
};
